'use client';

import './index.css';

import * as Sentry from '@sentry/nextjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/button';
import { Field } from '@/components/field';

import { isLink } from '@/helpers/storyblok/is-link';
import { linkResolver } from '@/helpers/storyblok/link-resolver';

export const AutoForm = ({ form, onSuccess }) => {
  const [error, setError] = useState(null);

  const { register, handleSubmit, formState, reset } = useForm();

  const destination = isLink(form.destination)
    ? linkResolver(form.destination)
    : null;

  const onSubmit = async (data) => {
    setError(null);

    try {
      const response = await fetch('/api/countdown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Form submit error`);
      }

      Sentry.withScope((scope) => {
        scope.setExtras(data);
        Sentry.captureMessage('Form submitted successfully');
      });

      onSuccess?.();
      reset();

      if (destination) {
        window.location.href = destination;
      }
    } catch (error) {
      setError(error);

      Sentry.withScope((scope) => {
        scope.setExtras(data);
        Sentry.captureException(error);
      });

      throw error;
    }
  };

  return (
    <form className="auto-form" onSubmit={handleSubmit(onSubmit)}>
      {form.lastNameField || form.firstNameField ? (
        <div className="auto-form__line">
          {form.firstNameField ? (
            <Field
              placeholder={form.firstNameField}
              autoComplete="given-name"
              {...register('firstName', {
                required: true,
              })}
              error={
                formState.errors.firstName
                  ? 'Veuillez renseigner votre prénom'
                  : null
              }
            />
          ) : null}

          {form.lastNameField ? (
            <Field
              placeholder={form.lastNameField}
              autoComplete="family-name"
              {...register('lastName', {
                required: true,
              })}
              error={
                formState.errors.lastName
                  ? 'Veuillez renseigner votre nom de famille'
                  : null
              }
            />
          ) : null}
        </div>
      ) : null}

      {form.emailLabel || form.phoneNumberField ? (
        <div className="auto-form__line">
          {form.emailLabel ? (
            <Field
              placeholder={form.emailLabel}
              type="email"
              autoComplete="email"
              {...register('email', {
                required: true,
              })}
              error={
                formState.errors.email
                  ? 'Veuillez renseigner votre e-mail'
                  : null
              }
            />
          ) : null}

          {form.phoneNumberField ? (
            <Field
              placeholder={form.phoneNumberField}
              type="tel"
              autoComplete="tel"
              {...register('phoneNumber')}
            />
          ) : null}
        </div>
      ) : null}

      {form.companyField || form.roleField ? (
        <div className="auto-form__line">
          {form.companyField ? (
            <Field
              placeholder={form.companyField}
              autoComplete="organization"
              {...register('company', {
                required: true,
              })}
              error={
                formState.errors.company
                  ? 'Veuillez renseigner le nom de votre entreprise'
                  : null
              }
            />
          ) : null}

          {form.roleField ? (
            <Field
              placeholder={form.roleField}
              autoComplete="role"
              {...register('role', {
                required: true,
              })}
              error={
                formState.errors.role
                  ? 'Veuillez renseigner votre fonction'
                  : null
              }
            />
          ) : null}
        </div>
      ) : null}

      <Button
        className="auto-form__cta"
        type="submit"
        loading={formState.isSubmitting}
      >
        {form.submitLabel && form.submitLabelMobile ? (
          <>
            <span className="desktop-only">{form.submitLabel}</span>
            <span className="mobile-only">{form.submitLabelMobile}</span>
          </>
        ) : (
          form.submitLabel || form.submitLabelMobile || 'Envoyer'
        )}
      </Button>

      {error ? (
        <p className="auto-form__error">
          {form.errorMessage ||
            "Une erreur est survenue lors de l'envoi du formulaire, veuillez réessayer plus tard."}
        </p>
      ) : null}
    </form>
  );
};
